import {
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type FocusEvent,
  type PointerEvent,
  type ReactElement,
} from 'react';
import { createPortal } from 'react-dom';
import { cx } from '@/lib/cx';

/**
 * A styled hover label for icon-only controls and disabled buttons.
 *
 * Owner decisions, 2026-09-24: every icon-only button and icon trigger gets a
 * hover label; it is themed through tokens, quick, shows on hover AND keyboard
 * focus, works on disabled buttons; the wording is the action's name, plus the
 * why-disabled reason when disabled; nothing extra on touch.
 *
 * ── HOW IT WORKS ON A DISABLED BUTTON ───────────────────────────────────────
 *
 * `Button` carries `disabled:pointer-events-none`, so a disabled button never
 * receives the hover — which is why a native `title` on it has never rendered.
 * The events are therefore taken on the WRAPPER span, which is not disabled.
 *
 * ── LAYER ────────────────────────────────────────────────────────────────────
 *
 * Portalled to `document.body`, `position: fixed`, on rung 65 of the layer
 * ladder in `src/index.css` — above portalled popovers (60) and overlays (50),
 * below the support-session frame (70), which that ladder reserves for one
 * occupant.
 *
 * ── PLACEMENT IS IDEMPOTENT ─────────────────────────────────────────────────
 *
 * The label is `w-max` capped by a max width, so its measured width does not
 * depend on where it currently sits — a fixed element's shrink-to-fit width
 * otherwise changes with `left`, and two passes would disagree (the `place()`
 * bug `Combobox` already records).
 */
export interface TooltipProps {
  label: string;
  children: ReactElement;
  /** Classes for the wrapper span (layout pass-through — see `Button`). */
  className?: string;
  /**
   * Whether the trigger is described by the label. Defaults to `true` unless
   * the label equals the child's `aria-label` — a label that only repeats the
   * accessible name would be announced twice.
   */
  describes?: boolean;
}

const SHOW_DELAY_MS = 300;
const GAP_PX = 6;
const MARGIN_PX = 8;

interface Placement {
  top: number;
  left: number;
}

export function Tooltip({ label, children, className, describes }: TooltipProps) {
  const id = useId();
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<number | null>(null);
  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState<Placement | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const hide = useCallback(() => {
    clearTimer();
    setOpen(false);
    setPlacement(null);
  }, [clearTimer]);

  useEffect(() => clearTimer, [clearTimer]);

  const onPointerEnter = (event: PointerEvent<HTMLSpanElement>) => {
    // Touch: nothing extra. A tap still acts; no long-press label.
    if (event.pointerType === 'touch') return;
    clearTimer();
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      setOpen(true);
    }, SHOW_DELAY_MS);
  };

  const onFocus = (event: FocusEvent<HTMLSpanElement>) => {
    // Keyboard focus only — a mouse click also focuses, and must not raise it.
    let visible = false;
    try {
      visible = (event.target as Element).matches(':focus-visible');
    } catch {
      visible = false;
    }
    if (!visible) return;
    clearTimer();
    setOpen(true);
  };

  const onBlur = (event: FocusEvent<HTMLSpanElement>) => {
    const next = event.relatedTarget as Node | null;
    if (next && wrapperRef.current?.contains(next)) return;
    hide();
  };

  // Escape, scroll and resize all dismiss it while open.
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') hide();
    };
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', hide, true);
    window.addEventListener('resize', hide);
    return () => {
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', hide, true);
      window.removeEventListener('resize', hide);
    };
  }, [open, hide]);

  useLayoutEffect(() => {
    if (!open) return;
    const trigger = wrapperRef.current;
    const tip = tipRef.current;
    if (!trigger || !tip) return;
    const rect = trigger.getBoundingClientRect();
    const width = tip.offsetWidth;
    const height = tip.offsetHeight;
    const vw = document.documentElement.clientWidth;
    const vh = document.documentElement.clientHeight;

    let top = rect.top - height - GAP_PX;
    if (top < MARGIN_PX) {
      top = rect.bottom + GAP_PX;
      if (top + height > vh - MARGIN_PX) top = Math.max(MARGIN_PX, vh - height - MARGIN_PX);
    }
    let left = rect.left + rect.width / 2 - width / 2;
    left = Math.min(Math.max(left, MARGIN_PX), Math.max(MARGIN_PX, vw - width - MARGIN_PX));
    setPlacement({ top, left });
  }, [open, label]);

  const childAriaLabel = isValidElement<{ 'aria-label'?: string }>(children)
    ? children.props['aria-label']
    : undefined;
  const isDescription = describes ?? label.trim() !== (childAriaLabel ?? '').trim();

  let trigger = children;
  if (open && isDescription && isValidElement<{ 'aria-describedby'?: string }>(children)) {
    const existing = children.props['aria-describedby'];
    trigger = cloneElement(children, {
      'aria-describedby': existing ? `${existing} ${id}` : id,
    });
  }

  const hasDisplay = /(^|\s)(hidden|block|flex|inline|inline-block|grid|contents|sr-only)(\s|$)/.test(
    className ?? '',
  );

  return (
    <span
      ref={wrapperRef}
      className={cx(!hasDisplay && 'inline-flex', className)}
      onPointerEnter={onPointerEnter}
      onPointerLeave={hide}
      onPointerDown={hide}
      onFocus={onFocus}
      onBlur={onBlur}
    >
      {trigger}
      {open &&
        label &&
        createPortal(
          <div
            ref={tipRef}
            id={id}
            role="tooltip"
            style={{
              top: placement?.top ?? 0,
              left: placement?.left ?? 0,
              visibility: placement ? 'visible' : 'hidden',
            }}
            className={cx(
              'pointer-events-none fixed z-[65] w-max max-w-[min(20rem,calc(100vw-16px))]',
              'rounded-md bg-fg px-2 py-1 text-xs font-medium leading-snug text-fg-inverse shadow-md',
              'whitespace-normal [overflow-wrap:anywhere]',
              'transition-opacity duration-100 ease-out motion-reduce:transition-none',
              placement ? 'opacity-100' : 'opacity-0',
            )}
          >
            {label}
          </div>,
          document.body,
        )}
    </span>
  );
}
